import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  useGetApiServiceListQuery,
  useChangeServiceStatusMutation,
  useDeleteApiServiceMutation,
  useGetAccountsQuery,
} from '../../../../redux/api';
import useApiServicePermissions from '../hooks/useApiServicePermissions';
import {
  buildApiServiceListParams,
  buildFilterOptions,
  clampPageIndex,
  DEFAULT_API_SERVICE_FILTERS,
  getPageRange,
  mapApiServiceListResponse,
  resolveServiceAccount,
} from '../utils/apiServiceUtils';
import { API_SERVICE_MANAGER_SOURCE } from '../apiServiceApiHelpers';
import { openWorkbenchWorkflow } from '../apiServiceWizardSession';
import { K, apiT } from '../apiServiceI18n';
import ApiServiceSearchPanel from './ApiServiceSearchPanel';
import ApiServiceTable from './ApiServiceTable';
import ApiServiceTablePagination from './ApiServiceTablePagination';
import ApiServiceBlankState from './ApiServiceBlankState';
import ApiServiceExecutionHistoryDrawer from './ApiServiceExecutionHistoryDrawer';
import ApiServiceDetailsDrawer from './ApiServiceDetailsDrawer';
import ApiServiceEditDrawer from './ApiServiceEditDrawer';
import ApiServicePerformanceLoader from '../performance/ApiServicePerformanceLoader';

export default function ApiServiceOverview({ onNavigatePerformance, onCreateApi }) {
  const { t } = useTranslation();
  const { account: baseAccount } = useSelector((state) => state.queryParams);
  const isAllMode = baseAccount?.toLowerCase() === 'all';
  const { canModify, canView, permissionsReady } = useApiServicePermissions();

  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_API_SERVICE_FILTERS);
  const [page, setPage] = useState(0);
  const [lastTotalCount, setLastTotalCount] = useState(0);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [detailsTarget, setDetailsTarget] = useState(null);
  const [editPanel, setEditPanel] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const pageSize = appliedFilters.pageSize ?? 50;

  const listParams = useMemo(
    () => buildApiServiceListParams(baseAccount, appliedFilters, page),
    [baseAccount, appliedFilters, page]
  );

  const canFetchList = !!baseAccount && canView;

  const listQuery = useGetApiServiceListQuery(listParams, { skip: !canFetchList });

  const accountsQuery = useGetAccountsQuery(undefined, { skip: !isAllMode });

  const [changeServiceStatus, changeStatusState] = useChangeServiceStatusMutation();
  const [deleteApiService, deleteState] = useDeleteApiServiceMutation();

  const accounts = useMemo(() => {
    const payload = accountsQuery.data;
    const ok = payload?.current_state === 'SUCCESS' || payload?.status === 'SUCCESS';
    if (!ok) return [];
    const raw = payload?.data?.accounts ?? payload?.accounts ?? [];
    return Array.isArray(raw) ? [...raw].sort((a, b) => String(a).localeCompare(String(b))) : [];
  }, [accountsQuery.data]);

  const { rows, totalCount } = useMemo(
    () => mapApiServiceListResponse(listQuery.data),
    [listQuery.data]
  );

  useEffect(() => {
    setPage(0);
  }, [baseAccount]);

  const isListLoading = listQuery.isLoading || listQuery.isFetching;

  useEffect(() => {
    if (!isListLoading && listQuery.isSuccess) {
      setLastTotalCount(totalCount);
    }
  }, [isListLoading, listQuery.isSuccess, totalCount]);

  useEffect(() => {
    if (isListLoading) return;
    const clamped = clampPageIndex(page, totalCount, pageSize);
    if (clamped !== page) {
      setPage(clamped);
    }
  }, [totalCount, pageSize, page, isListLoading]);

  const effectiveTotalCount =
    totalCount > 0 ? totalCount : isListLoading ? lastTotalCount : 0;

  const pageRange = useMemo(
    () => getPageRange(page, pageSize, effectiveTotalCount),
    [page, pageSize, effectiveTotalCount]
  );

  const showTableShell =
    isListLoading ||
    listQuery.isUninitialized ||
    effectiveTotalCount > 0 ||
    lastTotalCount > 0;
  const showBlankState = !isListLoading && totalCount === 0 && listQuery.isSuccess;
  const showPaginationFooter = pageRange.totalPages > 1;

  const filterOptions = useMemo(() => buildFilterOptions(rows), [rows]);

  const showAccountCol = isAllMode;

  const showToast = useCallback((message, severity = 'success') => {
    setToast({ open: true, message, severity });
  }, []);

  const hasActiveFilters = useMemo(
    () => JSON.stringify(appliedFilters) !== JSON.stringify(DEFAULT_API_SERVICE_FILTERS),
    [appliedFilters]
  );

  const onApplyFilters = useCallback((filters) => {
    setPage(0);
    setAppliedFilters({ ...filters });
  }, []);

  const onClearFilters = useCallback(() => {
    setPage(0);
    setAppliedFilters(DEFAULT_API_SERVICE_FILTERS);
  }, []);

  const onPageChange = useCallback((nextPage) => {
    setPage(Math.max(0, nextPage));
  }, []);

  const refetch = () => listQuery.refetch();

  const handleEdit = useCallback(
    (row) => {
      if (!canModify) return;
      setEditPanel({ row, mode: 'edit' });
    },
    [canModify]
  );

  const handleView = useCallback((row) => {
    setEditPanel({ row, mode: 'view' });
  }, []);

  const handleWorkflow = useCallback(
    (row) => {
      const account = resolveServiceAccount(baseAccount, row.account);
      if (!account || !row.workflowGroup || !row.workflowName) {
        showToast(t(K.API_SVC_WF_REQUIRED), 'error');
        return;
      }
      openWorkbenchWorkflow({
        account,
        workflowGroup: row.workflowGroup,
        workflowName: row.workflowName,
      });
    },
    [baseAccount, showToast, t]
  );

  const handleEditPanelClose = useCallback(
    ({ success } = {}) => {
      setEditPanel(null);
      if (success) {
        showToast(apiT(K.API_SVC_APPLY_SUCCESS));
        refetch();
      }
    },
    [showToast]
  );

  const onStatusToggle = async (row) => {
    const nextStatus = row.status === 'enabled' ? 'false' : 'true';
    try {
      const result = await changeServiceStatus({
        account: row.account,
        wfg_name: row.workflowGroup,
        wf_name: row.workflowName,
        service_status: nextStatus,
        request_ip: 'react-workbench',
        source: API_SERVICE_MANAGER_SOURCE,
      }).unwrap();
      const ok = result?.current_state === 'SUCCESS' || result?.status === 'SUCCESS';
      if (ok) {
        showToast(
          nextStatus === 'true'
            ? apiT(K.API_SVC_STATUS_ENABLED_TOAST, { 0: row.name })
            : apiT(K.API_SVC_STATUS_DISABLED_TOAST, { 0: row.name })
        );
        refetch();
      } else {
        showToast(result?.message || t(K.API_SVC_STATUS_UPDATE_FAILED), 'error');
      }
    } catch {
      showToast(t(K.API_SVC_STATUS_UPDATE_FAILED), 'error');
    }
  };

  const onConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      const result = await deleteApiService({
        account: pendingDelete.account,
        wfg_name: pendingDelete.workflowGroup,
        wf_name: pendingDelete.workflowName,
        source: API_SERVICE_MANAGER_SOURCE,
      }).unwrap();
      const ok = result?.current_state === 'SUCCESS' || result?.status === 'SUCCESS';
      if (ok) {
        showToast(apiT(K.API_SVC_DELETE_SUCCESS, { 0: pendingDelete.name }));
        refetch();
      } else {
        showToast(result?.message || t(K.API_SVC_DELETE_FAILED), 'error');
      }
    } catch {
      showToast(t(K.API_SVC_DELETE_FAILED), 'error');
    } finally {
      setPendingDelete(null);
    }
  };

  const stubAction = (labelKey) => (row) => {
    showToast(apiT(K.API_SVC_COMING_SOON, { 0: t(labelKey), 1: row.name }), 'info');
  };

  const apiError =
    listQuery.error?.data?.message ||
    listQuery.error?.error ||
    (listQuery.data &&
    listQuery.data?.current_state !== 'SUCCESS' &&
    listQuery.data?.status !== 'SUCCESS'
      ? listQuery.data?.message
      : null);

  const skeletonRowCount = pageSize;

  const countLabel =
    isListLoading && effectiveTotalCount === 0
      ? t(K.API_SVC_LOADING_APIS)
      : effectiveTotalCount === 1
        ? apiT(K.API_SVC_API_COUNT, { 0: effectiveTotalCount })
        : apiT(K.API_SVC_API_COUNT_PLURAL, { 0: effectiveTotalCount });

  const pageLabel =
    effectiveTotalCount > 0 && pageRange.totalPages > 1
      ? apiT(K.API_SVC_PAGE_OF, { 0: pageRange.page + 1, 1: pageRange.totalPages })
      : '';

  if (!permissionsReady) {
    return (
      <ApiServicePerformanceLoader
        variant="inline"
        message={t(K.API_SVC_LOADING_APIS)}
        minHeight={360}
      />
    );
  }

  if (!canView) {
    return (
      <Alert severity="warning">
        {t(K.API_SVC_NO_PERMISSION_VIEW)}
      </Alert>
    );
  }

  if (!baseAccount) {
    return (
      <Alert severity="warning">
        {t(K.API_SVC_ACCOUNT_MISSING)}
      </Alert>
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          {countLabel}
          {pageLabel}
        </Typography>
        {canModify && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={onCreateApi}>
            {t(K.API_SVC_CREATE_API)}
          </Button>
        )}
      </Stack>

      {apiError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {apiError}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <ApiServiceSearchPanel
          filterOptions={filterOptions}
          showAccountFilter={isAllMode}
          accounts={accounts}
          onApply={onApplyFilters}
          onClear={onClearFilters}
          hasActiveFilters={hasActiveFilters}
        />
      </Box>

      {showBlankState ? (
        <ApiServiceBlankState canModify={canModify} onCreateApi={onCreateApi} />
      ) : showTableShell ? (
        <Box
          sx={{
            border: '1px solid #d5d9e0',
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: '#f6f7fa',
          }}
        >
          <ApiServiceTable
            rows={isListLoading ? [] : rows}
            showAccountCol={showAccountCol}
            canModify={canModify}
            loading={isListLoading}
            skeletonRowCount={skeletonRowCount}
            paginated={showPaginationFooter}
            onStatusToggle={onStatusToggle}
            onDetails={(row) => setDetailsTarget(row)}
            onPerformance={(row) => {
              if (onNavigatePerformance) onNavigatePerformance(row);
              else stubAction(K.API_SVC_MENU_PERFORMANCE)(row);
            }}
            onWorkflow={handleWorkflow}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={(row) => setPendingDelete(row)}
            onExecutionHistory={(row) => setHistoryTarget(row)}
          />
          {showPaginationFooter && (
            <ApiServiceTablePagination
              page={page}
              pageSize={pageSize}
              totalCount={effectiveTotalCount}
              onPageChange={onPageChange}
              disabled={isListLoading}
            />
          )}
        </Box>
      ) : null}

      <ApiServiceDetailsDrawer
        target={detailsTarget}
        onClose={() => setDetailsTarget(null)}
      />

      <ApiServiceEditDrawer
        target={editPanel?.row ?? null}
        mode={editPanel?.mode ?? 'edit'}
        onClose={handleEditPanelClose}
      />

      <ApiServiceExecutionHistoryDrawer
        target={historyTarget}
        onClose={() => setHistoryTarget(null)}
      />

      <Dialog open={!!pendingDelete} onClose={() => setPendingDelete(null)}>
        <DialogTitle>{t(K.API_SVC_DELETE_TITLE)}</DialogTitle>
        <DialogContent>
          <Typography>
            {apiT(K.API_SVC_DELETE_CONFIRM, { 0: pendingDelete?.name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)} disabled={deleteState.isLoading}>
            {t(K.BTN_CANCEL)}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={onConfirmDelete}
            disabled={deleteState.isLoading}
          >
            {t(K.API_SVC_BTN_DELETE)}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((tState) => ({ ...tState, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((tState) => ({ ...tState, open: false }))}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
